import { PROJECT } from "../_constants/actionConstants";
import _ from "lodash";

const projInitialState = {
  openProjectDetails: {}, // Normalized by projectId
  openProjectLists: {}, // Multiple instances of projLists are normalized and indexed by tabId.
  projectUnitLists: {}, // Normalized by projectId
  projectStaffLists: {}, // Normalized by projectId
  projectAssignmentHistories: {}, // Normalied by projectId
  jobStamps: {}, // Normalized by projectId
  assignedProjectsForUser: [], // Array of { projectId, fullEa, eFis, projectLocation, description, userId, projectRoleDescription }
  projRoles: [],
  projTypes: [],
  miscSessionData: {},
  fetching: false,
};

export const reducer = (state = projInitialState, action) => {
  switch (action.type) {
    case PROJECT.ADD:
    case PROJECT.EDIT:
    case PROJECT.INITIATE:
    case PROJECT.FETCH_ALL:
    case PROJECT.FETCH_DETAILS:
    case PROJECT.FETCH_TYPES:
    case PROJECT.FETCH_SUGGESTIONS:
    case PROJECT.FETCH_ALL_DROPDOWN:
    case PROJECT.FETCH_JOBSTAMP:
    case PROJECT.FETCH_PROJECT_ASSIGNMENTS:
    case PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY:
    case PROJECT.FETCH_PROJECT_UNITS:
    case PROJECT.SAVE_PROJECT_ASSIGNMENTS:
    case PROJECT.SAVE_PROJECT_UNITS:
    case PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER:
    case PROJECT.FETCH_PROJECT_ROLES:
      return { ...state, fetching: true };

    case PROJECT.ADD_FULFILLED:
    case PROJECT.ADD_REJECTED:
    case PROJECT.EDIT_REJECTED:
    case PROJECT.INITIATE_FULFILLED:
    case PROJECT.INITIATE_REJECTED:
    case PROJECT.FETCH_DETAILS_REJECTED:
    case PROJECT.FETCH_TYPES_REJECTED:
    case PROJECT.FETCH_ALL_REJECTED:
    case PROJECT.FETCH_SUGGESTIONS_REJECTED:
    case PROJECT.FETCH_ALL_DROPDOWN_REJECTED:
    case PROJECT.FETCH_JOBSTAMP_REJECTED:
    case PROJECT.FETCH_PROJECT_ASSIGNMENTS_REJECTED:
    case PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY_REJECTED:
    case PROJECT.FETCH_PROJECT_UNITS_REJECTED:
    case PROJECT.SAVE_PROJECT_ASSIGNMENTS_FULFILLED:
    case PROJECT.SAVE_PROJECT_ASSIGNMENTS_REJECTED:
    case PROJECT.SAVE_PROJECT_UNITS_FULFILLED:
    case PROJECT.SAVE_PROJECT_UNITS_REJECTED:
    case PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER_REJECTED:
    case PROJECT.FETCH_PROJECT_ROLES_REJECTED:
      return { ...state, fetching: false };

    case PROJECT.FETCH_DETAILS_FULFILLED:
    case PROJECT.EDIT_FULFILLED: {
      const { projectId, projDetails } = action;
      if (!projectId) return { ...state, fetching: false };
      return {
        ...state,
        fetching: false,
        openProjectDetails: {
          ...state.openProjectDetails,
          [projectId]: projDetails,
        },
      };
    }

    case PROJECT.CLEAR_DETAILS: {
      const { projectId } = action;
      if (!projectId || !(projectId in state.openProjectDetails)) return { ...state };
      const openProjectDetails = _.cloneDeep(state.openProjectDetails);
      delete openProjectDetails[projectId];
      return { ...state, openProjectDetails };
    }

    case PROJECT.FETCH_ALL_FULFILLED: {
      if (action.tabId == null) return { ...state, fetching: false };

      // FETCH_ALL_BY_DISTRICT/UNIT/USER all funnel to this action.
      // Consider this list unique to each tab, since the list may
      // change upon user request.
      return {
        ...state,
        fetching: false,
        openProjectLists: {
          ...state.openProjectLists,
          [action.tabId]: action.payload,
        },
      };
    }

    // CLEAR_LIST must be called before calling any methods that change
    // individual project data.
    case PROJECT.CLEAR_LIST: {
      const { tabId } = action;
      if (tabId == null) return { ...state };

      let openProjectLists = _.cloneDeep(state.openProjectLists);
      if (tabId in openProjectLists) delete openProjectLists[tabId];

      let miscSessionData = _.cloneDeep(state.miscSessionData);
      if (tabId in miscSessionData) delete miscSessionData[tabId];

      return { ...state, openProjectLists, miscSessionData };
    }

    case PROJECT.SET_PROJECT_WAITINGFOR: {
      const { tabId, waitingFor } = action;
      if (tabId == null) return { ...state };
      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...state.miscSessionData[tabId],
            projectSearchWaitingFor: waitingFor,
          },
        },
      };
    }

    case PROJECT.FETCH_SUGGESTIONS_FULFILLED: {
      const { tabId, projectList, searchString } = action;
      if (tabId == null) return { ...state, fetching: false };

      if (
        tabId in state.miscSessionData &&
        state.miscSessionData[tabId].projectSearchWaitingFor &&
        state.miscSessionData[tabId].projectSearchWaitingFor === searchString
      )
        return {
          ...state,
          openProjectLists: {
            ...state.openProjectLists,
            [tabId]: projectList,
          },
          miscSessionData: {
            ...state.miscSessionData,
            [tabId]: {
              ...state.miscSessionData[tabId],
              projectSearchHistory: {
                ...state.miscSessionData[tabId].projectSearchHistory,
                [searchString]: projectList || [],
              },
            },
          },
        };
      else return { ...state, fetching: false };
    }

    case PROJECT.FETCH_ALL_PAGED_FULFILLED: {
      // resetList takes whatever is incoming as the entire projectList.
      // resetList will be true when filters are cleared, etc., and will be
      // false when paging thru the data. Going to next page will fetch
      // additional data and append to existing employeeList.
      const { tabId, projectList, resetList, waitingFor } = action;
      if (tabId == null) return { ...state, fetching: false };

      // ProjectList fetches for both "browsing" and "searching" direct to this
      // action. Request is for "browsing" if the search string (i.e. the EA) is
      // blank, and "searching" if otherwise. For browsing, we simply update
      // the state with new list (if resetList is true), or append the next page
      // of data. Browsing data is not cached here.
      // But if the fetch is for searching (which is then checked against
      // projectSearchWaitingFor), cache the results. Caching in miscSessionData
      // is done slightly differently here compared to AdminUsers,
      // in that the search string is only unique within a district filter. For
      // example, when searching for EA by string "0K", it is cached not just by
      // "0K", but as a district-searchString combination based on the
      // currently-set district filter (e.g. "12-0K"). If no district filter is
      // provided, use 0.
      const miscSessionData =
        waitingFor &&
        tabId in state.miscSessionData &&
        state.miscSessionData[tabId].projectSearchWaitingFor &&
        state.miscSessionData[tabId].projectSearchWaitingFor === waitingFor
          ? {
              ...state.miscSessionData,
              [tabId]: {
                ...state.miscSessionData[tabId],
                projectSearchHistory: {
                  ...state.miscSessionData[tabId].projectSearchHistory,
                  [waitingFor]: resetList
                    ? projectList || []
                    : [
                        ...(state.miscSessionData[tabId].projectSearchHistory[
                          waitingFor
                        ] || []),
                        ...projectList,
                      ],
                },
              },
            }
          : { ...state.miscSessionData };
      return {
        ...state,
        fetching: false,
        openProjectLists: {
          ...state.openProjectLists,
          [tabId]: resetList
            ? projectList
            : [...(state.openProjectLists[tabId] || []), ...projectList],
        },
        miscSessionData,
      };
    }

    case PROJECT.FETCH_TYPES_FULFILLED: {
      if (!action.projTypes) return { ...state, fetching: false };
      return {
        ...state,
        fetching: false,
        projTypes: action.projTypes,
      };
    }

    case PROJECT.FETCH_JOBSTAMP_FULFILLED:
      if (!action.projectId || !action.jobStamp) return { ...state, fetching: false };
      return {
        ...state,
        fetching: false,
        jobStamps: {
          ...state.jobStamps,
          [action.projectId]: action.jobStamp,
        },
      };

    case PROJECT.FETCH_PROJECT_UNITS_FULFILLED:
      if (!action.projectId) return { ...state, fetching: false };

      // Consider this list unique to each tab, since the list may
      // change upon user request.
      return {
        ...state,
        projectUnitLists: {
          ...state.projectUnitLists,
          [action.projectId]: action.projUnitList || [],
        },
        fetching: false,
      };

    case PROJECT.FETCH_PROJECT_ASSIGNMENTS_FULFILLED: {
      const { projectId, projStaffList, projAssignmentHistoryList } = action;
      if (!projectId) return { ...state, fetching: false };
      // Consider this list unique to each project, and let it
      // serve as the single source of truth for all projStaffList needs
      return {
        ...state,
        projectStaffLists: {
          ...state.projectStaffLists,
          [projectId]: projStaffList,
        },
        ...(projAssignmentHistoryList && {
          ...state.projectAssignmentHistories,
          [projectId]: projAssignmentHistoryList,
        }),
        fetching: false,
      };
    }

    case PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY_FULFILLED: {
      const { projectId, projAssignmentHistoryList } = action;
      return {
        ...state,
        projectAssignmentHistories: {
          ...state.projectAssignmentHistories,
          [projectId]: projAssignmentHistoryList,
        },
        fetching: false,
      };
    }

    // case PROJECT.SAVE_PROJECT_UNITS_FULFILLED:
    //   if (!action.projectId) return { ...state, fetching: false };
    //   return {
    //     ...state,
    //     projectUnitLists: {
    //       ...state.projectUnitLists,
    //       [action.projectId]: action.projUnitList || [],
    //     },
    //     projectStaffLists: {
    //       ...state.projectStaffLists,
    //       [action.projectId]: action.projStaffList || [],
    //     },
    //     fetching: false,
    //   };

    case PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER_FULFILLED:
      return {
        ...state,
        assignedProjectsForUser: action.payload,
        fetching: false,
      };

    case PROJECT.FETCH_PROJECT_ROLES_FULFILLED:
      return { ...state, fetching: false, projRoles: action.payload };

    case PROJECT.CLEAR_ALL_PROJECT_DATA:
      return projInitialState;

    default:
      return state;
  }
};
